import { useEffect, useRef } from "react"
import { TPaymentSession } from "../utils/@types/data/paymentSession"

type Socket = any

type RejoinErrorReason =
  | "missing_old_socket_id"
  | "unauthorized"
  | (string & {})

type Args = {
  socket: Socket | null
  active: boolean
  // TTL do registro de sessão no backend (default 30min). PIX expira antes (15min),
  // então o rejoin é válido durante todo o ciclo de vida do pagamento.
  sessionMaxAgeMs?: number
  onRejoined?: (payload: { room?: string }) => void
  onRejoinError?: (reason: RejoinErrorReason) => void
}

const readPaymentSession = (): TPaymentSession | null => {
  try {
    const raw = localStorage.getItem("paymentSession")
    if (!raw) return null
    const parsed = JSON.parse(raw) as TPaymentSession
    if (!parsed?.extref || !parsed?.socketId) return null
    return parsed
  } catch {
    return null
  }
}

export function usePaymentRejoin({
  socket,
  active,
  sessionMaxAgeMs = 30 * 60 * 1000,
  onRejoined,
  onRejoinError,
}: Args) {
  const onRejoinedRef = useRef(onRejoined)
  const onRejoinErrorRef = useRef(onRejoinError)

  onRejoinedRef.current = onRejoined
  onRejoinErrorRef.current = onRejoinError

  useEffect(() => {
    if (!active || !socket) return

    const tryRejoin = () => {
      const session = readPaymentSession()
      if (!session) return

      const startedAt = Number(session.paymentStartedAt) || 0
      if (startedAt && Date.now() - startedAt > sessionMaxAgeMs) {
        // Registro provavelmente expirou no backend; não tenta.
        return
      }

      socket.emit("rejoinPaymentSession", {
        oldSocketId: session.socketId,
        extref: session.extref,
      })
    }

    const handleRejoined = (payload: { room?: string }) => {
      onRejoinedRef.current?.(payload ?? {})
    }

    const handleRejoinError = (payload: { reason?: RejoinErrorReason }) => {
      // eslint-disable-next-line no-console
      console.warn("[payment] rejoinError", payload?.reason)
      onRejoinErrorRef.current?.(payload?.reason ?? "unauthorized")
    }

    // Se o socket já estiver conectado, emite imediatamente.
    if (socket.connected) tryRejoin()

    socket.on("connect", tryRejoin)
    socket.on("rejoined", handleRejoined)
    socket.on("rejoinError", handleRejoinError)

    return () => {
      socket.off("connect", tryRejoin)
      socket.off("rejoined", handleRejoined)
      socket.off("rejoinError", handleRejoinError)
    }
  }, [socket, active, sessionMaxAgeMs])
}
